<template>
	<li
		:class="[ menuItemClass, { 'active' : isOpen } ]"
		ref="flyout"
	>
		<template v-if="!isSecondaryMenu || ( isSecondaryMenu && items.length )">
			<button
				type="button"
				@click="toggleMenu"
				@keydown.enter.prevent="toggleMenu"
				@keydown.space.prevent="toggleMenu"
				@keydown.arrow-down.prevent="openAndFocusFirst"
				@mouseenter="handleMouseEnter"
				@mouseleave="handleMouseLeave"
				:aria-expanded="isOpen.toString()"
				aria-haspopup="menu"
				:aria-controls="'submenu-' + itemId"
				class="tw-capitalize"
				:class="[
					showSubMenu && isMobile ? 'tw-bg-white tw-px-3 tw-pt-2 tw-pb-2 lg:tw-pb-5 tw-rounded-t-md tw-text-blue-900 hover:tw-text-blue-900' : '',
					isMobile && getItemLink( overviewLink, currentPath) ? 'tw-text-blue-900 tw-bg-white tw-rounded-md tw-py-2' : 'tw-text-white',
					showSubMenu && isMobile && getItemLink( overviewLink, currentPath) ? ' tw-rounded-t-md tw-rounded-b-none' : '',
					isMobile && isSecondaryMenu && hasMenuIcon( itemData ) ? ' tw-w-auto' : 'tw-w-full',
					isMobile ? 'tw-text-white tw-w-full tw-flex tw-items-center tw-justify-between tw-px-1 sm:tw-px-3 tw-font-bold' : 'tw-text-gray-500 hover:tw-text-gray-900 border-ortus'
				]"
			>
				<i
					:class="getMenuIcon( itemData )"
					v-if="hasMenuIcon( itemData )"
					aria-hidden="true"
				></i>

				<span v-else class="tw-w-1/2">{{ label }}</span>

				<i
					v-if="!isSecondaryMenu || ( isMobile && isSecondaryMenu )"
					class="fas tw-h-4 tw-w-1/2 lg:tw-w-4 md:tw-ml-1 tw-text-right lg:tw-ml-0 lg:tw-text-center"
					aria-hidden="true"
					:class="[
						showSubMenu && isMobile ? 'fa-angle-up' : 'fa-angle-down',
						isMobile && isSecondaryMenu ? 'tw-ml-3' : ''
					]"
				></i>
			</button>

			<ul
				class="tw-bg-white submenu"
				:class="[
					{ 'show': isOpen },
					isMobile ? 'submenu-mobile tw-py-5 tw-rounded-b-md' : 'tw-shadow-lg tw-rounded-lg',
					isMobile && isSecondaryMenu ? 'tw-mx-0 tw-px-2 lg:tw-mx-auto' : ''
				]"
				:id="'submenu-' + itemId"
				role="menu"
				@mouseenter="handleMouseEnter"
				@mouseleave="handleMouseLeave"
				@keydown="handleMenuKeydown"
			>
				<!-- Overview link for primary menus only -->
				<li
					class="tw-pb-2"
					v-if="!isSecondaryMenu"
					role="none"
				>
					<a
						tabindex="0"
						role="menuitem"
						:href="overviewLink"
						:aria-label="'Overview of ' + label"
						class="tw-flex tw-w-full hover:tw-bg-blue-900 tw-py-2"
						:class="[
							isMobile ? 'tw-text-blue-900 tw-pl-3' : 'tw-text-gray-500',
							overviewLink == currentPath ? 'tw-bg-gray-200 tw-flex' : ''
						]"
					>
						Overview
					</a>
				</li>

				<template v-if="!isSecondaryMenu">
					<li
						v-for="( item, itemIdx ) in items.slice( 0, 9 )"
						:key="'sub-' + itemIdx"
						class="tw-pb-2"
						role="none"
					>
						<a
							:href="item.link"
							role="menuitem"
							tabindex="0"
							:aria-label="item.title"
							class="tw-flex tw-w-full hover:tw-bg-blue-900 tw-py-2"
							:class="[
								isMobile ? 'tw-text-blue-900 tw-pl-3' : 'tw-text-gray-500',
								item.link == currentPath ? 'tw-bg-gray-200' : ''
							]"
						>
							{{ item.title }}
						</a>
					</li>
				</template>

				<template v-else>
					<li
						v-for="( item, itemIdx ) in items"
						:key="'sub-' + itemIdx"
						class="tw-pb-2 tw-list-none"
						role="none"
					>
						<a
							:href="item.url"
							role="menuitem"
							tabindex="0"
							:aria-label="item.label"
							class="tw-flex tw-w-full hover:tw-bg-blue-900 tw-py-2 tw-text-base"
							:class="[
								isMobile ? 'tw-text-blue-900 tw-pl-3' : 'tw-text-gray-500'
							]"
						>
							{{ item.label }}
						</a>
					</li>
				</template>

				<li
					v-if="items.length > 9"
					role="none"
				>
					<a
						tabindex="0"
						role="menuitem"
						:href="overviewLink"
						:aria-label="'View All ' + label"
						class="tw-text-blue-500"
						:class="isMobile ? 'tw-pl-3' : ''"
					>
						View All {{ label }}
					</a>
				</li>
			</ul>
		</template>

		<!-- Item without submenu: only one link -->
		<template v-else>
			<a
				:href="overviewLink"
				target="_self"
				:aria-label="label"
				:class="isMobile ? 'tw-text-white' : 'tw-text-gray-500 hover:tw-text-gray-900 border-ortus'"
			>
				<i
					:class="getMenuIcon( itemData )"
					aria-hidden="true"
					v-if="hasMenuIcon( itemData )"
				></i>
				<span v-else>{{ label }}</span>
			</a>
		</template>
	</li>
</template>

<script>
export default {
	props: {
		columns: {
			type: Number,
			default: 1
		},
		items: {
			type: Array,
			required: true,
			default: () => []
		},
		label: {
			type: String,
			required: true
		},
		menuItemClass: { type: String, default: "" },
		overviewLink: { type: String, default: "" },
		isMobile: {
			type: Boolean,
			default: false
		},
		isSecondaryMenu: {
			type: Boolean,
			default: false
		},
		itemData: { type: String, default: "" },
		// The father controls which menu is open.
		activeMenuId: { type: String, default: "" }
	},
	data() {
		return {
			itemId: Math.round( Math.random() * 10000000 ) + "",
			focusIndex: -1
		};
	},
	computed: {
		// isOpen is derived from the parent-controlled activeMenuId
		isOpen() {
			return this.activeMenuId === this.itemId;
		},
		showSubMenu() {
			return this.isOpen;
		},
		columnClass() {
			return "md:tw-grid-cols-" + this.columns;
		},
		currentPath() {
			return window.location.href;
		}
	},
	created() {
		// Close when clicking outside the component
		window.addEventListener( "click", this.handleOutsideClick );
		// Close with global escape
		window.addEventListener( "keydown", this.handleGlobalKeydown );
	},
	beforeDestroy() {
		window.removeEventListener( "click", this.handleOutsideClick );
		window.removeEventListener( "keydown", this.handleGlobalKeydown );
	},
	methods: {
		getMenuItems() {
			if ( !this.$el ) return [];
			return Array.from( this.$el.querySelectorAll( '[role="menuitem"]' ) );
		},
		openAndFocusFirst() {
			this.$emit( "set-active", this.itemId );
			this.$nextTick( () => {
				const items = this.getMenuItems();
				if ( items.length ) {
					items[0].focus();
					this.focusIndex = 0;
				}
			} );
		},
		handleMouseEnter() {
			if ( !this.isMobile ) {
				// The parent cancels any pending closing timeouts.
				this.$emit( "mouse-enter", this.itemId );
			}
		},
		handleMouseLeave() {
			if ( !this.isMobile ) {
				// The father decides whether to close with a coordinated delay
				this.$emit( "mouse-leave", this.itemId );
			}
		},
		handleGlobalKeydown( e ) {
			if ( e.key === "Escape" && this.isOpen ) {
				this.$emit( "set-active", null );
				// Return focus to the button that opened the menu
				const btn = this.$el.querySelector( "button" );
				if ( btn ) btn.focus();
			}
		},
		handleMenuKeydown( e ) {
			const items = this.getMenuItems();
			if ( !items.length ) return;

			switch ( e.key ) {
				case "ArrowDown":
					e.preventDefault();
					this.focusIndex = ( this.focusIndex + 1 ) % items.length;
					items[this.focusIndex].focus();
					break;
				case "ArrowUp":
					e.preventDefault();
					this.focusIndex = ( this.focusIndex - 1 + items.length ) % items.length;
					items[this.focusIndex].focus();
					break;
				case "Home":
					e.preventDefault();
					this.focusIndex = 0;
					items[0].focus();
					break;
				case "End":
					e.preventDefault();
					this.focusIndex = items.length - 1;
					items[this.focusIndex].focus();
					break;
				case "Escape":
					this.$emit( "set-active", null );
					this.$el.querySelector( "button" )?.focus();
					break;
				case "Tab":
					// Close the submenu on exit with Tab
					this.$emit( "set-active", null );
					break;
			}
		},
		toggleMenu() {
			// If it's already open, they close it; if not, they open it.
			this.$emit( "set-active", this.isOpen ? null : this.itemId );
		},
		handleOutsideClick( e ) {
			if ( this.$refs.flyout && !this.$refs.flyout.contains( e.target ) ) {
				if ( this.isOpen ) {
					this.$emit( "set-active", null );
				}
			}
		},
		getItemLink( pathOverviewLink, currentPath ) {
			return pathOverviewLink.split( "/" )[3] == currentPath.split( "/" )[3];
		},
		hasMenuIcon( data ) {
			return data && JSON.parse( data ).icon;
		},
		getMenuIcon( data ) {
			return data && JSON.parse( data ).icon ? JSON.parse( data ).icon : "";
		}
	}
};
</script>